/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import { _ as e } from "../../chunks/tslib.es6.js";
import t from "../../Basemap.js";
import i from "../../core/Collection.js";
import s from "../../core/Loadable.js";
import { watch as a, on as r, when as o } from "../../core/reactiveUtils.js";
import { property as n } from "../../core/accessorSupport/decorators/property.js";
import { cast as p } from "../../core/accessorSupport/decorators/cast.js";
import "../../core/arrayUtils.js";
import "../../core/has.js";
import { subclass as c } from "../../core/accessorSupport/decorators/subclass.js";
import { canProjectWithoutEngine as m, isLoaded as l, load as u } from "../../geometry/projection.js";
import { equals as d } from "../../geometry/support/spatialReferenceUtils.js";
import { contentEquals as h } from "../../support/basemapUtils.js";
import { default3DCompatibility as f, default2DCompatibility as v } from "./support/basemapCompatibilityUtils.js";
import y from "./support/BasemapGalleryItem.js";
import b from "./support/LocalBasemapsSource.js";
import B from "./support/PortalBasemapsSource.js";
const g = i.ofType(y);
function _(e) {
  return e && "esri.portal.Portal" === e.declaredClass;
}
function w(e) {
  return e && !(e instanceof B) && (!!e.portal || !!e.query);
}
function j(e) {
  return e && "basemaps" in e && "state" in e && "refresh" in e;
}
let I = class extends s {
  constructor(e) {
    super(e), this._loadingProjectionEngine = !1, this._originalActiveBasemap = null, this.includeCurrentBasemap = !1, this.items = new g(), this.source = new B(), this.view = null;
  }
  initialize() {
    const e = () => this._recreateItems();
    this.addHandles([a(() => [this.view, this.view?.ready], () => {
      this.view?.ready && (this._originalActiveBasemap = this.activeBasemap);
    }, {
      initial: !0
    }), a(() => "ready" === this.state ? this.compatibilityFunction : null, () => this._updateItems()), a(() => [this._effectiveIncludeCurrentBasemap, this._originalActiveBasemap], e), r(() => this.source?.basemaps, "change", e, {
      onListenerAdd: e
    }), o(() => this.view, () => {
      this.source instanceof B && (this.source.viewType = this.view?.type);
    }, {
      once: !0
    })]);
  }
  destroy() {
    const e = this.source.basemaps.find(e => e === this.activeBasemap);
    e && this.source.basemaps.remove(e), this.source?.destroy();
  }
  get _effectiveIncludeCurrentBasemap() {
    return this.includeCurrentBasemap && this.source?.basemaps.every(e => "loading" !== e.loadStatus);
  }
  get activeBasemap() {
    return this.view?.map?.basemap ?? null;
  }
  set activeBasemap(e) {
    const i = this.view;
    if (!i?.map) return;
    const s = "string" == typeof e ? t.fromId(e) : e;
    if (!s || !i.ready) return i.map.basemap = s, void this._clearOverride("activeBasemap");
    const a = s.spatialReference || this.items?.find(e => this.basemapEquals(s, e.basemap))?.spatialReference;
    if (a && "spatialReferenceLocked" in i && !i.spatialReferenceLocked) {
      const t = i.spatialReference;
      if (null != a && !d(t, a) && !m(i.spatialReference, a) && !l()) return this._override("activeBasemap", s), this._loadingProjectionEngine = !0, void u().then(() => {
        this._get("activeBasemap") === e && (i.map.basemap = e, i.spatialReference = a, this._clearOverride("activeBasemap"));
      }, () => {}).then(() => {
        this._loadingProjectionEngine = !1;
      });
      i.map.basemap = s, this._clearOverride("activeBasemap"), null == a || d(i.spatialReference, a) || (i.spatialReference = a);
    } else i.map.basemap = s, this._clearOverride("activeBasemap");
  }
  get activeBasemapIndex() {
    const {
      state: e,
      activeBasemap: t
    } = this;
    return "ready" !== e ? -1 : this._findBasemapIndex(t);
  }
  get compatibilityFunction() {
    return "3d" === this.view?.type ? f : v;
  }
  set compatibilityFunction(e) {
    this._overrideIfSome("compatibilityFunction", e);
  }
  castSource(e) {
    return Array.isArray(e) || i.isCollection(e) ? new b({
      basemaps: e
    }) : _(e) ? new B({
      portal: e
    }) : w(e) ? new B(e) : j(e) ? e : null;
  }
  get state() {
    return this.view?.ready && this.source ? this._loadingProjectionEngine ? "loading" : "ready" : "disabled";
  }
  basemapEquals(e, t) {
    return h(e, t);
  }
  refresh() {
    this._recreateItems();
  }
  load(e) {
    return this.addResolvingPromise(s.isLoadable(this.source) ? this.source.load(e) : null), Promise.resolve(this);
  }
  _findBasemapIndex(e) {
    const {
        items: t
      } = this,
      i = t.findIndex(t => t.basemap === e);
    return -1 === i ? t.findIndex(t => this.basemapEquals(t.basemap, e)) : i;
  }
  _recreateItems() {
    const e = this.source?.basemaps,
      {
        view: t,
        compatibilityFunction: i
      } = this,
      s = new Map(this.items.map(e => [e.basemap, e]));
    function a(e) {
      const a = s.get(e);
      return a ? (s.delete(e), a) : new y({
        basemap: e,
        compatibilityFunction: i,
        view: t
      });
    }
    this.items.removeAll(), e && this.items.addMany(e.map(a));
    const r = -1 !== this._findBasemapIndex(this._originalActiveBasemap);
    this._effectiveIncludeCurrentBasemap && !r && null != this._originalActiveBasemap && this.items.unshift(a(this._originalActiveBasemap)), s.forEach(e => e.destroy());
  }
  _updateItems() {
    for (const e of this.items) e.compatibilityFunction = this.compatibilityFunction, e.view = this.view;
  }
};
e([n()], I.prototype, "_effectiveIncludeCurrentBasemap", null), e([n()], I.prototype, "_loadingProjectionEngine", void 0), e([n()], I.prototype, "_originalActiveBasemap", void 0), e([n()], I.prototype, "activeBasemap", null), e([n({
  readOnly: !0
})], I.prototype, "activeBasemapIndex", null), e([n()], I.prototype, "compatibilityFunction", null), e([n()], I.prototype, "includeCurrentBasemap", void 0), e([n({
  readOnly: !0,
  type: g
})], I.prototype, "items", void 0), e([n()], I.prototype, "source", void 0), e([p("source")], I.prototype, "castSource", null), e([n({
  readOnly: !0
})], I.prototype, "state", null), e([n()], I.prototype, "view", void 0), I = e([c("esri.widgets.BasemapGallery.BasemapGalleryViewModel")], I);
const A = I;
export { A as default };